import { useEffect, useState } from "react";

import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ComboBoxControl } from "@/Common/Components/Inputs/ComboboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";

import styles from "./ExternalBillingEmployeeSelectPopup.module.scss";

interface ExternalBillingEmployeeSelectPopupProps {
    encounterEmployeeIds: string[],
    otherEmployeeIds: string[],
    primaryClinicians?: string[],
    onCancel: () => void;
    onEmployeeSelect: (employeeId: string) => void;
}

function ExternalBillingEmployeeSelectPopup(props: ExternalBillingEmployeeSelectPopupProps): JSX.Element {
    const { getText } = useTranslation();
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
    const [combinedEmployeeList, setCombinedEmployeeList] = useState(Immutable.List<ComboBoxDropDownItem>());

    useEffect(() => {
        const getExternalEmployee = async (): Promise<void> => {
            const combinedList = Immutable.List<ComboBoxDropDownItem>([
                ...(props.encounterEmployeeIds ?? [])
                    .map(employeeId => EmployeeUIModel.fromCompositeObject(cacheStore.GetEmployeeByContactFacetId(employeeId)))
                    .map(employee => new ComboBoxDropDownItem(
                        employee.ContactFacetId,
                        isStarClinician(employee.ContactFacetId)
                            ? `★ ${employee.nameWithPrefix()}`
                            : employee.nameWithPrefix(),
                        0,
                        `${getText("ExternalBillingEmployeeSelect_ProvidersWithinNoteLabel")}:`
                    )).sort((a, b) => a.DisplayName.localeCompare(b.DisplayName)),
                ...(props.otherEmployeeIds ?? [])
                    .map(employeeId => EmployeeUIModel.fromCompositeObject(cacheStore.GetEmployeeByContactFacetId(employeeId)))
                    .map(employee => new ComboBoxDropDownItem(
                        employee.ContactFacetId,
                        isStarClinician(employee.ContactFacetId)
                            ? `★ ${employee.nameWithPrefix()}`
                            : employee.nameWithPrefix(),
                        0,
                        `${getText("ExternalBillingEmployeeSelect_AllOthersProvidersLabel")}:`
                    )).sort((a, b) => a.DisplayName.localeCompare(b.DisplayName))
            ]);
            setCombinedEmployeeList(combinedList);
            setSelectedEmployeeId(combinedList?.map(p => p.Code).first());
        };
        getExternalEmployee();
    }, [props.encounterEmployeeIds, props.otherEmployeeIds, props.primaryClinicians]);

    function onEmployeeSelect(): void {
        props.onCancel();
        props.onEmployeeSelect(selectedEmployeeId);
    }

    function isStarClinician(employeeContactId: string): boolean {
        return props.primaryClinicians?.contains(employeeContactId.toLocaleLowerCase());
    }

    function footer(): JSX.Element {
        return (
            <div className="display-flex-justified-end">
                <div className="margin-xs-left">
                    <button
                        className="btn btn-default"
                        data-cy="billable-providers-cancel"
                        data-action="cancel"
                        onClick={() => props.onCancel()}
                    >
                        {getText("CancelLabel")}
                    </button>
                </div>
                <div className="margin-xs-left">
                    <button
                        className="btn btn-primary"
                        data-cy="billable-providers-save"
                        onClick={() => onEmployeeSelect()}
                    >
                        {getText("SaveLabel")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Modal
                showOverlay
                fixedOverlayPosition
                showX
                centerContent
                width={550}
                title={getText("ExternalBillingEmployeeSelect_ChooseBillableProviderLabel")}
                onClose={() => props.onCancel()}
                footer={footer()}
                disableOverlayClose
                draggable
            >
                <div className="p-body" data-cy="billable-providers-popup">
                    <div className={`row  ${styles.paddingLeft}`}>
                        <p>{getText("ExternalBillingEmployeeSelect_ChooseBillableProviderDescriptionLabel")}</p>
                    </div>
                    <br />
                    <div className={`row  ${styles.paddingLeft}`}>
                        <p>{getText("ExternalBillingEmployeeSelect_ChooseBillableNoProviderDescriptionLabel")}</p>
                    </div>
                    <div className={`row  ${styles.fieldContainer}`}>
                        <div className="col-md-6 col-lg-6 vcenter text-right">
                            <label className={`form-label ${styles.billableProvidersLabel}`}>{getText("ExternalBillingEmployeeSelect_BillableProviderListLabel")}</label>
                        </div>
                        <div className="col-md-6 col-lg-6 inline-block">
                            {!!combinedEmployeeList?.count()
                                ? (
                                    <ComboBoxControl
                                        id="billableProviders"
                                        selectedCode={selectedEmployeeId}
                                        items={combinedEmployeeList}
                                        hasEmptyItem={false}
                                        onChange={value => { setSelectedEmployeeId(value); }}
                                        className="form-control myle-control"
                                    />
                                )
                                : getText("ExternalBillingEmployeeSelect_NoProviderAreBillableLabel")
                            }
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export { ExternalBillingEmployeeSelectPopup };
